import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import cx from 'classnames';

import { ButtonV3 } from 'components/ComponentV2';
import { GPTActionMenu } from 'components/candidate/GPTActionMenu/GPTActionMenu.component';
import IconButton from 'components/IconButton';
import Icon from 'components/Icon';
import Tooltip from 'components/Tooltip';
import { UserActionEnum } from 'hooks/useOpportunity/useOpportunityInternal';
import { useResponsive } from 'hooks/useResponsive';
import { TouchpointType } from 'lib/models/touchpoint';
import { useOpportunity } from 'lib/providers/OpportunityProvider';
import { useAuth } from 'lib/providers/AuthProvider';
import { StudentSocietyAttributes } from 'lib/models/student-society';

import styles from './FloatingActionButtons.module.scss';

const CompanyNotifyMeFooter = ({ className }: { className?: string }) => {
  const [t] = useTranslation('opportunity');
  const { creatableForId, creatableForName, creatableForFollowingId, updateUserAction, isPageFullyScrolled } = useOpportunity();

  return (
    <footer className={cx(styles.footer, className, { [styles.scrolled]: isPageFullyScrolled })}>
      <div className={styles['follow-message-container']}>
        <Icon iconName="icon_info" size="mediumplus" />
        <p className={styles.text}>{t('company-notify-me-footer.message', { organization_name: creatableForName })}</p>
      </div>
      {creatableForFollowingId ? (
        <ButtonV3
          className={cx(styles.cta, styles['secondary-icon'])}
          color="secondary"
          size="large"
          startIcon="icon_check-double"
          onClick={() => updateUserAction({ userAction: UserActionEnum.UNFOLLOW_COMPANY, userActionId: creatableForFollowingId })}
        >
          {t('company-notify-me-footer.cta')}
        </ButtonV3>
      ) : (
        <ButtonV3
          className={styles.cta}
          color="primary"
          size="large"
          startIcon="icon_notification-bell"
          onClick={() => updateUserAction({ userAction: UserActionEnum.FOLLOW_COMPANY, userActionId: creatableForId })}
        >
          {t('company-notify-me-footer.cta')}
        </ButtonV3>
      )}
    </footer>
  );
};

const SocietyJoinFooter = ({ className }: { className?: string; isPageFullyScrolled?: boolean }) => {
  const [t] = useTranslation('opportunity');
  const { creatableForId, creatableForName, creatableFor, updateUserAction, isPageFullyScrolled } = useOpportunity();
  const { subscribed, awaiting_approval, subscribers_require_approval } = creatableFor as StudentSocietyAttributes;
  const awaitingApproval = subscribers_require_approval && awaiting_approval;

  return (
    <footer className={cx(styles.footer, className, { [styles.scrolled]: isPageFullyScrolled })}>
      <div className={styles['follow-message-container']}>
        <Icon iconName="icon_info" size="mediumplus" />
        <p className={styles.text}>{t('society-join-footer.message', { organization_name: creatableForName })}</p>
      </div>
      {subscribed || awaitingApproval ? (
        <ButtonV3
          className={cx(styles.cta, styles['secondary-icon'])}
          color="secondary"
          size="large"
          startIcon={awaitingApproval ? 'icon_check' : 'icon_check-double'}
          onClick={() => updateUserAction({ userAction: UserActionEnum.DISJOIN_SOCIETY, userActionId: creatableForId })}
        >
          {awaitingApproval ? t('society-join-footer.requested-cta') : t('society-join-footer.member-cta')}
        </ButtonV3>
      ) : (
        <ButtonV3
          className={styles.cta}
          color="primary"
          size="large"
          startIcon="icon_plus"
          onClick={() => updateUserAction({ userAction: UserActionEnum.JOIN_SOCIETY, userActionId: creatableForId })}
        >
          {t('society-join-footer.cta')}
        </ButtonV3>
      )}
    </footer>
  );
};

export const FloatingActionButtons = ({
  className,
  prevButton,
  nextButton,
  handleNext,
  handlePrev,
  ctaClassName,
}: {
  className?: string;
  prevButton?: ReactElement;
  nextButton?: ReactElement;
  handleNext?: () => void;
  handlePrev?: () => void;
  ctaClassName?: string;
}) => {
  const [t] = useTranslation('opportunity');
  const screens = useResponsive();
  const {
    opportunity,
    isOpportunityLoading,
    isFollowLoading,
    isFollowing,
    isOpportunityUnappliable,
    willJoinWaitlist,
    queryUserAction,
    creatableForType,
    isOpportunityAppliedOrUnappliable,
    isPageFullyScrolled,
    handleShareURL,
    updateUserAction,
  } = useOpportunity();
  const { isInitialAuthLoading } = useAuth();
  const { id, title, slug, touchpointable_type, is_applied, is_waitlisted, apply_url, is_eligible_for_auto_apply } = opportunity;
  const isEvent = touchpointable_type === TouchpointType.Event;
  const showGPTActionMenu = !isEvent && !isOpportunityAppliedOrUnappliable;

  const application = useMemo(() => {
    return {
      id,
      title,
      touchpointable_type,
      slug,
    };
  }, [id, title, touchpointable_type, slug]);

  const [canInteractThruKeyboard, setCanInteractThruKeyboard] = useState(
    typeof document !== 'undefined' ? document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA' : false
  );
  const disableSave = isFollowLoading && queryUserAction === UserActionEnum.SAVE;
  const disableRegister = isFollowLoading && queryUserAction === UserActionEnum.REGISTER;
  const disableApply = isFollowLoading && queryUserAction === UserActionEnum.APPLY;
  const disableSaveTooltip = isFollowing || !canInteractThruKeyboard || disableSave;
  const disableRegisterTooltip = !isEvent || (isEvent && is_applied) || !canInteractThruKeyboard || disableRegister || willJoinWaitlist;
  const disableApplyTooltip = isEvent || (!isEvent && is_applied) || !canInteractThruKeyboard || disableApply;

  useEffect(() => {
    const handleFocus = () => {
      setCanInteractThruKeyboard(document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA');
    };

    // Add event listeners for focus and blur events
    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('blur', handleFocus, true);

    // Cleanup the event listeners on unmount
    return () => {
      document.removeEventListener('focus', handleFocus, true);
      document.removeEventListener('blur', handleFocus, true);
    };
  }, []);

  useEffect(() => {
    function handleKeydown(event: KeyboardEvent) {
      if (!canInteractThruKeyboard || queryUserAction) {
        return;
      }
      switch (event.key) {
        case 's':
        case 'S':
          if (disableSaveTooltip || isOpportunityUnappliable) return;
          updateUserAction({ userAction: UserActionEnum.SAVE });
          break;
        case 'r':
        case 'R':
          if (disableRegisterTooltip || isOpportunityUnappliable) return;
          updateUserAction({ userAction: UserActionEnum.REGISTER });
          break;
        case 'a':
        case 'A':
          if (disableApplyTooltip || isOpportunityUnappliable) return;
          updateUserAction({ userAction: UserActionEnum.APPLY });
          break;
        case 'ArrowRight':
          if (handleNext) handleNext();
          break;
        case 'ArrowLeft':
          if (handlePrev) handlePrev();
          break;
      }
    }

    document.addEventListener('keydown', handleKeydown);

    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [
    canInteractThruKeyboard,
    isOpportunityUnappliable,
    queryUserAction,
    disableSaveTooltip,
    disableRegisterTooltip,
    disableApplyTooltip,
    handleNext,
    handlePrev,
    updateUserAction,
  ]);

  if (isOpportunityLoading || isInitialAuthLoading) {
    return null;
  }

  if (isOpportunityUnappliable) {
    return creatableForType === 'company' ? <CompanyNotifyMeFooter className={className} /> : <SocietyJoinFooter className={className} />;
  }

  return (
    <div className={cx(styles.container, className, { [styles.scrolled]: isPageFullyScrolled })}>
      {prevButton}
      <IconButton
        color="secondary"
        iconName="share"
        className={cx(styles['icon-cta'], ctaClassName, styles['secondary-icon'])}
        size="xmediumplus"
        onClick={handleShareURL}
      />
      {showGPTActionMenu && (
        <GPTActionMenu showIcon menuButtonClassName={cx(styles['gpt-action-menu-icon'], ctaClassName)} application={application} />
      )}
      {isFollowing ? (
        <ButtonV3
          size="large"
          color="secondary"
          startIcon="icon_bookmark-filled"
          onClick={() => updateUserAction({ userAction: UserActionEnum.UNSAVE })}
          className={cx(styles['save-button'], styles.cta, ctaClassName, styles['secondary-icon'])}
          disabled={isFollowLoading && queryUserAction === UserActionEnum.UNSAVE}
        >
          {t('floating-action-btns.saved')}
        </ButtonV3>
      ) : !screens.sm ? (
        <Tooltip
          disabled={disableSaveTooltip}
          content={
            <p className={styles.tooltip}>
              <Trans t={t} i18nKey="floating-action-btns.save-tooltip">
                1<span className={styles.strong}>S</span>2
              </Trans>
            </p>
          }
          showTippy
          color="dark"
          xAlign="center"
          position="top"
        >
          <ButtonV3
            size="large"
            color="secondary"
            startIcon="icon_bookmark"
            onClick={() => updateUserAction({ userAction: UserActionEnum.SAVE })}
            className={cx(styles['save-button'], styles.cta, ctaClassName, styles['secondary-icon'])}
            disabled={disableSave}
          >
            {t('floating-action-btns.save')}
          </ButtonV3>
        </Tooltip>
      ) : (
        <ButtonV3
          size="large"
          color="secondary"
          startIcon="icon_bookmark"
          onClick={() => updateUserAction({ userAction: UserActionEnum.SAVE })}
          className={cx(styles['save-button'], styles.cta, ctaClassName, styles['secondary-icon'])}
          disabled={disableSave}
        >
          {t('floating-action-btns.save')}
        </ButtonV3>
      )}
      {isEvent ? (
        is_applied ? (
          is_waitlisted ? (
            <ButtonV3 className={cx(styles.cta, styles.disabled, ctaClassName)} color="primary" startIcon="icon_check-double" size="large">
              {t('floating-action-btns.waitlisted')}
            </ButtonV3>
          ) : (
            <ButtonV3 className={cx(styles.cta, styles.disabled, ctaClassName)} color="primary" startIcon="icon_check-double" size="large">
              {t('floating-action-btns.registered')}
            </ButtonV3>
          )
        ) : !screens.sm ? (
          <Tooltip
            disabled={disableRegisterTooltip}
            content={
              <p className={styles.tooltip}>
                <Trans t={t} i18nKey="floating-action-btns.register-tooltip">
                  1<span className={styles.strong}>R</span>2
                </Trans>
              </p>
            }
            showTippy
            color="dark"
            xAlign="center"
            position="top"
          >
            <ButtonV3
              className={cx(styles.cta, ctaClassName)}
              color="primary"
              startIcon="icon_bolt-filled"
              size="large"
              startIconClassName={apply_url ? undefined : styles['easy-register']}
              onClick={() => updateUserAction({ userAction: UserActionEnum.REGISTER })}
              disabled={disableRegister}
            >
              {willJoinWaitlist
                ? t('floating-action-btns.join-waitlist')
                : apply_url
                ? t('floating-action-btns.register')
                : t('floating-action-btns.easy-register')}
            </ButtonV3>
          </Tooltip>
        ) : (
          <ButtonV3
            className={cx(styles.cta, ctaClassName)}
            color="primary"
            startIcon="icon_bolt-filled"
            size="large"
            startIconClassName={apply_url ? undefined : styles['easy-register']}
            onClick={() => updateUserAction({ userAction: UserActionEnum.REGISTER })}
            disabled={disableRegister}
          >
            {willJoinWaitlist
              ? t('floating-action-btns.join-waitlist')
              : apply_url
              ? t('floating-action-btns.register')
              : t('floating-action-btns.easy-register')}
          </ButtonV3>
        )
      ) : is_applied ? (
        <ButtonV3 className={cx(styles.cta, styles.disabled, ctaClassName)} color="primary" startIcon="icon_check-double" size="large">
          {t('floating-action-btns.applied')}
        </ButtonV3>
      ) : !screens.sm ? (
        <Tooltip
          disabled={disableApplyTooltip}
          content={
            <p className={styles.tooltip}>
              <Trans t={t} i18nKey="floating-action-btns.apply-tooltip">
                1<span className={styles.strong}>A</span>2
              </Trans>
            </p>
          }
          showTippy
          color="dark"
          xAlign="center"
          position="top"
        >
          <ButtonV3
            className={cx(styles.cta, ctaClassName)}
            size="large"
            color="primary"
            startIcon={'icon_bolt-filled'}
            startIconClassName={cx({ [styles['quick-apply']]: is_eligible_for_auto_apply })}
            onClick={() => updateUserAction({ userAction: UserActionEnum.APPLY })}
            disabled={disableApply}
          >
            {is_eligible_for_auto_apply ? t('floating-action-btns.quick-apply') : t('floating-action-btns.apply')}
          </ButtonV3>
        </Tooltip>
      ) : (
        <ButtonV3
          className={cx(styles.cta, ctaClassName)}
          size="large"
          color="primary"
          startIcon={'icon_bolt-filled'}
          startIconClassName={cx({ [styles['quick-apply']]: is_eligible_for_auto_apply })}
          onClick={() => updateUserAction({ userAction: UserActionEnum.APPLY })}
          disabled={disableApply}
        >
          {is_eligible_for_auto_apply ? t('floating-action-btns.quick-apply') : t('floating-action-btns.apply')}
        </ButtonV3>
      )}
      {nextButton}
    </div>
  );
};

FloatingActionButtons.displayName = 'FloatingActionButtons';
